<template>
  <Teleport to="body">
    <div
      ref="scroller"
      class="fixed inset-0 z-50 overflow-y-auto overscroll-contain"
      style="background-color: rgb(0 0 0 / 0.85)"
      role="dialog"
      aria-modal="true"
      :aria-label="`${feed.profile.name} Instagramissa`"
      data-jest="instagram-modal"
      @click.self="emit('close')"
      @keydown.esc="emit('close')"
    >
      <button
        ref="closeButton"
        type="button"
        class="fixed top-3 right-3 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        aria-label="Sulje"
        data-jest="instagram-modal-close"
        @click="emit('close')"
      >
        <XMarkIcon class="size-7" />
      </button>

      <div class="mx-auto grid w-full max-w-[560px] gap-6 px-3 py-14 sm:px-0" @click.self="emit('close')">
        <article
          v-for="post in feed.posts"
          :id="`ig-post-${post.id}`"
          :key="post.id"
          class="overflow-hidden rounded-lg bg-surface-raised text-default shadow-lg"
          data-jest="instagram-modal-post"
        >
          <header class="flex items-center gap-3 px-4 py-3">
            <img
              v-if="feed.profile.image"
              :src="feed.profile.image"
              alt=""
              width="36"
              height="36"
              loading="lazy"
              class="size-9 rounded-full ring-2 ring-red-800 ring-offset-1"
            >
            <span class="flex-1 font-semibold">{{ feed.profile.username }}</span>
            <a
              :href="feed.profile.url"
              target="_blank"
              rel="noopener"
              class="text-sm font-semibold text-red-800 no-underline hover:underline"
            >Seuraa</a>
          </header>

          <InstagramModalMedia :post="post" />

          <div class="grid gap-2 px-4 pb-4 pt-3 text-sm">
            <p v-if="typeof post.likeCount === 'number'" class="font-semibold" data-jest="instagram-likes">
              {{ formatLikes(post.likeCount) }}
            </p>
            <p v-if="postText(post)" class="whitespace-pre-line break-words">
              <span class="font-semibold">{{ feed.profile.username }}</span>
              {{ ' ' }}
              <template v-for="(part, i) in splitHashtags(postText(post))" :key="i">
                <span v-if="part.hashtag" class="text-red-800">{{ part.text }}</span>
                <template v-else>
                  {{ part.text }}
                </template>
              </template>
            </p>
            <div class="flex items-center justify-between gap-4 pt-1 text-xs text-muted">
              <time :datetime="post.timestamp" class="uppercase tracking-wide">
                {{ formatDate(post.timestamp) }}
              </time>
              <a
                :href="post.permalink"
                target="_blank"
                rel="noopener"
                class="font-medium text-muted hover:text-default"
              >Näytä Instagramissa</a>
            </div>
          </div>
        </article>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { XMarkIcon } from '@heroicons/vue/24/outline'
import InstagramModalMedia from '~/components/InstagramModalMedia.vue'
import type { InstagramFeed, InstagramPost } from '~/types/instagram'

const props = defineProps<{
  feed: InstagramFeed
  /** The post the modal opens at */
  startPostId: string
}>()
const emit = defineEmits<{ close: [] }>()

const scroller = ref<HTMLElement | null>(null)
const closeButton = ref<HTMLButtonElement | null>(null)
let previousFocus: HTMLElement | null = null
let previousOverflow = ''

const postText = (post: InstagramPost) => post.text ?? post.caption

/** "#tarvike" parts highlighted like in Instagram */
const splitHashtags = (text: string) => text.split(/(#[\p{L}\p{N}_]+)/u).filter(Boolean).map(part => ({ text: part, hashtag: part.startsWith('#') }))

const formatLikes = (count: number) => `${count.toLocaleString('fi-FI')} ${count === 1 ? 'tykkäys' : 'tykkäystä'}`

const formatDate = (timestamp: string) => {
  const date = new Date(timestamp)
  return date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'long', ...(date.getFullYear() !== new Date().getFullYear() && { year: 'numeric' }) })
}

onMounted(() => {
  previousFocus = document.activeElement as HTMLElement | null
  previousOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  closeButton.value?.focus({ preventScroll: true })
  // Open at the clicked post
  nextTick(() => document.getElementById(`ig-post-${props.startPostId}`)?.scrollIntoView({ block: 'start' }))
})

onBeforeUnmount(() => {
  document.body.style.overflow = previousOverflow
  previousFocus?.focus?.({ preventScroll: true })
})
</script>
